import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { ModalContent, ModalCloseButton } from '../common';
import Button from '../common/Button';
import { FormSection, FormGrid, FormField, Input, Select, Textarea, NumberInput } from '../common/Form';
import { Heading, Text } from '../common/Typography';
import { supabase } from '../../utils/supabase';
import { createSessionBlock } from '../../services/blockBookingService';
import { format, addDays, addWeeks, parseISO, isBefore, startOfDay } from 'date-fns';
import SessionConfigFields, { templateToSessionConfig } from './SessionConfigFields';
import {
  PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
  normalizePegBoardQueueReturn,
} from '../../utils/pegBoard/constants';
import {
  DEFAULT_SESSION_START_TIME,
  DEFAULT_SESSION_END_TIME,
  getDefaultEndTime,
} from '../../utils/sessionTimeUtils';

/**
 * BlockBookingModal - Modal for creating recurring session blocks
 * Supports template-based or from-scratch creation with flexible recurrence patterns
 */
const BlockBookingModal = ({
  isOpen,
  onClose,
  clubId,
  templates = [],
  onSuccess
}) => {
  const [step, setStep] = useState(1); // 1: Form (with optional template), 2: Preview
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    // Block metadata
    blockName: '',
    blockDescription: '',
    
    // Session details
    session_type_id: '',
    name: '',
    description: '',
    max_players: '16',
    number_of_courts: '1',
    price_per_player: '',
    court_booking_cost: '',
    location: '',
    min_elo_level: '',
    start_time: DEFAULT_SESSION_START_TIME,
    end_time: DEFAULT_SESSION_END_TIME,
    currency_code: 'GBP',

    // Signup settings
    signup_deadline_hours: '',
    waitlist_enabled: true,
    waitlist_promotion_type: 'first_come_first_served',
    signup_selection: 'first_come',
    is_public: false,
    game_type_restriction: 'all_types',
    enable_player_categorisation: false,
    competitive_match_format: 'doubles',
    points_target: 21,
    deuce_mode: 'standard',
    peg_board_selection_window: 6,
    peg_board_queue_return: PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
    session_status: 'draft',
    session_series_id: '',
    
    // Recurrence settings
    recurrence_pattern: 'weekly', // 'weekly', 'biweekly', 'custom'
    recurrence_interval: 7, // days
    start_date: '',
    num_occurrences: '6',
    days_of_week: [] // For multi-day per week: [0,2,4] = Sun, Tue, Thu
  });

  const [previewSessions, setPreviewSessions] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  // Get current user on mount
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Get player ID from user
        const { data: playerData } = await supabase
          .from('players')
          .select('id')
          .eq('auth_id', user.id)
          .single();
        
        if (playerData) {
          setCurrentUser(playerData.id);
        }
      }
    };
    getCurrentUser();
  }, []);

  // Load session types
  useEffect(() => {
    if (isOpen) {
      fetchSessionTypes();
    }
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedTemplate(null);
      setError(null);
      setPreviewSessions([]);
      setFormData({
        blockName: '',
        blockDescription: '',
        session_type_id: '',
        name: '',
        description: '',
        max_players: '16',
        number_of_courts: '1',
        price_per_player: '',
        court_booking_cost: '',
        location: '',
        min_elo_level: '',
        start_time: DEFAULT_SESSION_START_TIME,
        end_time: DEFAULT_SESSION_END_TIME,
        currency_code: 'GBP',
        signup_deadline_hours: '',
        waitlist_enabled: true,
        waitlist_promotion_type: 'first_come_first_served',
        signup_selection: 'first_come',
        is_public: false,
        game_type_restriction: 'all_types',
        enable_player_categorisation: false,
        competitive_match_format: 'doubles',
        points_target: 21,
        deuce_mode: 'standard',
        peg_board_selection_window: 6,
        peg_board_queue_return: PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
        session_status: 'draft',
        session_series_id: '',
        recurrence_pattern: 'weekly',
        recurrence_interval: 7,
        start_date: '',
        num_occurrences: '6',
        days_of_week: []
      });
    }
  }, [isOpen]);

  const fetchSessionTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('session_types')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setSessionTypes(data || []);
    } catch (err) {
      console.error('Error fetching session types:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear field error when user types
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleTemplateSelect = (templateId) => {
    if (!templateId) {
      setSelectedTemplate(null);
      return;
    }
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      
      // Auto-fill form with template data
      setFormData(prev => ({
        ...prev,
        session_type_id: template.session_type_id || '',
        name: template.session_name || '',
        description: template.description || '',
        max_players: template.max_players?.toString() || '16',
        number_of_courts: template.number_of_courts?.toString() || '1',
        price_per_player: template.price_per_player?.toString() || '',
        court_booking_cost: template.court_booking_cost?.toString() || '',
        location: template.location || '',
        min_elo_level: template.min_elo_level?.toString() || '',
        start_time: template.start_time ? template.start_time.substring(0, 5) : DEFAULT_SESSION_START_TIME,
        end_time: template.end_time ? template.end_time.substring(0, 5) : getDefaultEndTime(
          template.start_time ? template.start_time.substring(0, 5) : DEFAULT_SESSION_START_TIME
        ),
        currency_code: template.currency_code || 'GBP',
        signup_deadline_hours: template.signup_deadline_hours?.toString() || '',
        waitlist_enabled: template.waitlist_enabled !== false,
        waitlist_promotion_type: template.waitlist_promotion_type || 'first_come_first_served',
        signup_selection: template.signup_selection || 'first_come',
        is_public: template.is_public || false,
        blockName: template.session_name ? `${template.session_name} Block` : '',
        game_type_restriction: template.game_type_restriction || 'all_types',
        enable_player_categorisation: template.enable_player_categorisation || false,
        competitive_match_format: template.competitive_match_format || 'doubles',
        peg_board_selection_window: template.peg_board_selection_window ?? 6,
        peg_board_queue_return: normalizePegBoardQueueReturn(template.peg_board_queue_return),
      }));
    }
  };

  const handleRecurrencePatternChange = (pattern) => {
    const intervals = {
      weekly: 7,
      biweekly: 14,
      custom: 7
    };
    
    setFormData(prev => ({
      ...prev,
      recurrence_pattern: pattern,
      recurrence_interval: intervals[pattern]
    }));
  };

  const calculateSessionDates = () => {
    const { start_date, recurrence_pattern, recurrence_interval, num_occurrences, days_of_week } = formData;
    
    if (!start_date || !num_occurrences) {
      return [];
    }

    const dates = [];
    const startDate = parseISO(start_date);
    const occurrences = parseInt(num_occurrences);

    if (days_of_week.length > 0) {
      // Multiple days per week
      let currentWeek = startDate;
      const weeksNeeded = Math.ceil(occurrences / days_of_week.length);
      
      for (let week = 0; week < weeksNeeded; week++) {
        for (const dayOfWeek of days_of_week.sort((a, b) => a - b)) {
          if (dates.length >= occurrences) break;
          
          const daysToAdd = (dayOfWeek - currentWeek.getDay() + 7) % 7;
          const sessionDate = addDays(currentWeek, daysToAdd);
          
          // Only add if it's today or in the future
          if (!isBefore(sessionDate, startOfDay(new Date()))) {
            dates.push(sessionDate);
          }
        }
        currentWeek = addWeeks(currentWeek, recurrence_pattern === 'biweekly' ? 2 : 1);
      }
    } else {
      // Single day pattern
      for (let i = 0; i < occurrences; i++) {
        let sessionDate;
        
        if (recurrence_pattern === 'weekly') {
          sessionDate = addWeeks(startDate, i);
        } else if (recurrence_pattern === 'biweekly') {
          sessionDate = addWeeks(startDate, i * 2);
        } else {
          // Custom interval
          sessionDate = addDays(startDate, i * parseInt(recurrence_interval));
        }
        
        dates.push(sessionDate);
      }
    }

    return dates.slice(0, occurrences);
  };

  // Derived last session date for helper text (when start date + number of sessions are set)
  const lastSessionDate = (() => {
    const dates = calculateSessionDates();
    return dates.length > 0 ? dates[dates.length - 1] : null;
  })();

  const validateForm = () => {
    const errors = {};
    
    // Block metadata
    if (!formData.blockName.trim()) {
      errors.blockName = 'Block name is required';
    }
    
    // Session details
    if (!formData.name.trim()) {
      errors.name = 'Session name is required';
    }
    if (!formData.session_type_id) {
      errors.session_type_id = 'Session type is required';
    }
    if (!formData.max_players || parseInt(formData.max_players, 10) < 1) {
      errors.max_players = 'Max players must be at least 1';
    }
    if (!formData.number_of_courts || parseInt(formData.number_of_courts, 10) < 1) {
      errors.number_of_courts = 'Number of courts must be at least 1';
    }
    if (!formData.start_time) {
      errors.start_time = 'Start time is required';
    }
    if (!formData.end_time) {
      errors.end_time = 'End time is required';
    }
    
    // Validate end time is after start time
    if (formData.start_time && formData.end_time) {
      const [startHour, startMin] = formData.start_time.split(':').map(Number);
      const [endHour, endMin] = formData.end_time.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      if (endMinutes <= startMinutes) {
        errors.end_time = 'End time must be after start time';
      }
    }
    
    // Recurrence settings
    if (!formData.start_date) {
      errors.start_date = 'Start date is required';
    } else {
      const startDate = parseISO(formData.start_date);
      if (isBefore(startDate, startOfDay(new Date()))) {
        errors.start_date = 'Start date must be today or in the future';
      }
    }
    
    if (!formData.num_occurrences || parseInt(formData.num_occurrences) < 1) {
      errors.num_occurrences = 'Number of sessions must be at least 1';
    } else if (parseInt(formData.num_occurrences) > 52) {
      errors.num_occurrences = 'Number of sessions cannot exceed 52';
    }
    
    if (formData.recurrence_pattern === 'custom' && (!formData.recurrence_interval || parseInt(formData.recurrence_interval) < 1)) {
      errors.recurrence_interval = 'Interval must be at least 1 day';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateForm()) {
      // Generate preview
      const dates = calculateSessionDates();
      const sessionTypeName = sessionTypes.find(t => t.id === formData.session_type_id)?.name || '';
      
      const sessions = dates.map((date, index) => ({
        date: format(date, 'yyyy-MM-dd'),
        displayDate: format(date, 'EEE, MMM d, yyyy'),
        name: formData.name,
        type: sessionTypeName,
        start_time: formData.start_time,
        end_time: formData.end_time,
        max_players: formData.max_players,
        index: index + 1
      }));
      
      setPreviewSessions(sessions);
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (previewSessions.length === 0) {
      setError('No sessions to create');
      return;
    }

    if (!currentUser) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare session data
      const sessionsToCreate = previewSessions.map(session => ({
        club_id: clubId,
        session_date: session.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        name: formData.name,
        description: formData.description,
        max_players: parseInt(formData.max_players),
        number_of_courts: parseInt(formData.number_of_courts),
        price_per_player: formData.price_per_player ? parseFloat(formData.price_per_player) : null,
        court_booking_cost: formData.court_booking_cost ? parseFloat(formData.court_booking_cost) : null,
        currency_code: formData.currency_code,
        location: formData.location,
        min_elo_level: formData.min_elo_level ? parseInt(formData.min_elo_level) : null,
        session_type_id: formData.session_type_id,
        session_status: 'draft',
        signup_deadline_hours: formData.signup_deadline_hours ? parseInt(formData.signup_deadline_hours) : null,
        waitlist_enabled: true,
        waitlist_promotion_type: formData.waitlist_promotion_type,
        signup_selection: formData.signup_selection === 'host_select' ? 'host_select' : 'first_come',
        is_public: formData.is_public,
        game_type_restriction: formData.game_type_restriction,
        enable_player_categorisation: formData.enable_player_categorisation,
        competitive_match_format: formData.competitive_match_format || 'doubles',
        points_target: formData.points_target ?? 21,
        deuce_mode: formData.deuce_mode || 'standard',
        peg_board_selection_window: formData.peg_board_selection_window ?? 6,
        peg_board_queue_return: normalizePegBoardQueueReturn(formData.peg_board_queue_return),
        session_series_id: formData.session_series_id || null,
      }));

      // Create the block
      const result = await createSessionBlock({
        clubId,
        blockName: formData.blockName,
        description: formData.blockDescription,
        recurrencePattern: formData.recurrence_pattern,
        recurrenceInterval: parseInt(formData.recurrence_interval),
        sessions: sessionsToCreate,
        createdBy: currentUser
      });

      if (result.success) {
        onSuccess?.(result);
        onClose();
      } else {
        throw new Error(result.error || 'Failed to create block booking');
      }
    } catch (err) {
      console.error('Error creating block booking:', err);
      setError(err.message || 'Failed to create block booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-6 flex items-center justify-center">
      <div className="flex items-center space-x-2">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
            step >= 1 ? 'bg-primary text-text-inverse' : 'bg-surface-subtle text-text-secondary'
          }`}
        >
          1
        </div>
        <div className={`h-0.5 w-12 ${step >= 2 ? 'bg-primary' : 'bg-border-medium'}`} />
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
            step >= 2 ? 'bg-primary text-text-inverse' : 'bg-surface-subtle text-text-secondary'
          }`}
        >
          2
        </div>
      </div>
    </div>
  );

  const renderFormStep = () => (
    <div className="space-y-6">
      <Heading level={3} className="text-text-primary">
        Session Details & Recurrence
      </Heading>

      {/* Block Metadata - at top */}
      <FormSection title="Block Information">
        <FormGrid columns={1}>
          <FormField label="Block Name" required error={fieldErrors.blockName} helpText="A name to identify this group of recurring sessions">
            <Input
              name="blockName"
              value={formData.blockName}
              onChange={handleInputChange}
              placeholder="e.g., Saturday Sessions - Spring 2026"
            />
          </FormField>
          <FormField label="Description" error={fieldErrors.blockDescription}>
            <Textarea
              name="blockDescription"
              value={formData.blockDescription}
              onChange={handleInputChange}
              placeholder="Optional description for this block booking"
              rows={2}
            />
          </FormField>
        </FormGrid>
      </FormSection>

      <SessionConfigFields
        value={formData}
        onChange={(next) => setFormData(prev => ({ ...prev, ...next }))}
        clubId={clubId}
        sessionTypes={sessionTypes}
        templates={templates}
        selectedTemplateId={selectedTemplate?.id || ''}
        onTemplateSelect={(template) => {
          setSelectedTemplate(template);
          if (template) {
            setFormData(prev => ({
              ...prev,
              ...templateToSessionConfig(template),
              blockName: prev.blockName || (template.session_name ? `${template.session_name} Block` : '')
            }));
          }
        }}
        fieldErrors={fieldErrors}
        isModal={true}
        showSessionTypeDescription={false}
        nameLabel="Session Name"
      />

      {/* Recurrence Pattern */}
      <FormSection title="Recurrence Pattern">
        <FormGrid columns={1}>
          <FormField label="Recurrence Type" required>
            <div className="space-y-2">
              <label className="flex cursor-pointer items-center space-x-2">
                <input
                  type="radio"
                  name="recurrence_pattern"
                  value="weekly"
                  checked={formData.recurrence_pattern === 'weekly'}
                  onChange={(e) => handleRecurrencePatternChange(e.target.value)}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-text-primary">Weekly - Every week on the same day</span>
              </label>
              <label className="flex cursor-pointer items-center space-x-2">
                <input
                  type="radio"
                  name="recurrence_pattern"
                  value="biweekly"
                  checked={formData.recurrence_pattern === 'biweekly'}
                  onChange={(e) => handleRecurrencePatternChange(e.target.value)}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-text-primary">Bi-weekly - Every 2 weeks on the same day</span>
              </label>
              <label className="flex cursor-pointer items-center space-x-2">
                <input
                  type="radio"
                  name="recurrence_pattern"
                  value="custom"
                  checked={formData.recurrence_pattern === 'custom'}
                  onChange={(e) => handleRecurrencePatternChange(e.target.value)}
                  className="text-primary focus:ring-primary"
                />
                <span className="text-text-primary">Custom - Specify custom interval in days</span>
              </label>
            </div>
          </FormField>

          {formData.recurrence_pattern === 'custom' && (
            <FormField label="Interval (days)" required error={fieldErrors.recurrence_interval}>
              <NumberInput
                name="recurrence_interval"
                value={formData.recurrence_interval}
                onChange={handleInputChange}
                min="1"
                placeholder="e.g., 10 for every 10 days"
              />
            </FormField>
          )}

          <FormGrid columns={2}>
            <FormField label="Start Date" required error={fieldErrors.start_date}>
              <Input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </FormField>
            <FormField label="Number of Sessions" required error={fieldErrors.num_occurrences} helpText="Maximum 52 sessions">
              <NumberInput
                name="num_occurrences"
                value={formData.num_occurrences}
                onChange={handleInputChange}
                min="1"
                max="52"
              />
            </FormField>
          </FormGrid>

          {lastSessionDate && (
            <p className="mt-2 text-sm text-text-secondary">
              Last session will be: <strong className="text-text-primary">{format(lastSessionDate, 'EEEE, d MMMM yyyy')}</strong>
            </p>
          )}
        </FormGrid>
      </FormSection>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div>
        <Heading level={3} className="text-text-primary">
          Review Session Preview
        </Heading>
        <Text className="mt-2 text-text-secondary">
          {previewSessions.length} session{previewSessions.length !== 1 ? 's' : ''} will be created as drafts. Publish each one when you want it visible for signups.
        </Text>
      </div>

      <div className="max-h-96 overflow-y-auto rounded-lg border border-border-medium bg-surface-subtle p-4">
        <div className="space-y-2">
          {previewSessions.map((session, index) => (
            <div
              key={index}
              className="bg-field flex items-center justify-between rounded-md border border-border-medium p-3"
            >
              <div className="flex-1">
                <div className="font-medium text-text-primary">
                  Session {session.index}: {session.name}
                </div>
                <div className="mt-1 text-sm text-text-secondary">
                  {session.displayDate} • {session.start_time} - {session.end_time}
                </div>
                {session.type && (
                  <div className="mt-1 text-xs text-text-muted">
                    {session.type} • Max {session.max_players} players
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger-light p-4">
          <Text className="text-sm text-danger-dark">{error}</Text>
        </div>
      )}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent
        size="large"
        padding={false}
        className="flex max-h-[90vh] flex-col overflow-hidden border border-border-medium shadow-xl"
      >
        <div className="relative flex shrink-0 items-start justify-between gap-4 border-b border-border-medium px-6 py-4 pr-14">
          <div>
            <Heading level={2} className="text-text-primary">
              Create Block Booking
            </Heading>
            <Text className="mt-1 text-sm text-text-secondary">Create multiple recurring sessions at once</Text>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {renderStepIndicator()}

          <div className="mt-6">
            {step === 1 && renderFormStep()}
            {step === 2 && renderPreviewStep()}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border-medium bg-surface-subtle px-6 py-4">
          <div>
            {step > 1 && (
              <Button onClick={handleBack} variant="outline" disabled={loading}>
                Back
              </Button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <Button onClick={onClose} variant="outline" disabled={loading}>
              Cancel
            </Button>
            {step < 2 && (
              <Button onClick={handleNext} variant="primary" disabled={loading}>
                Next
              </Button>
            )}
            {step === 2 && (
              <Button
                onClick={handleSubmit}
                variant="primary"
                disabled={loading || previewSessions.length === 0}
              >
                {loading ? 'Creating...' : `Create ${previewSessions.length} Sessions`}
              </Button>
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default BlockBookingModal;
